import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { GripVertical, ListChecks, Pencil, Plus, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { PageHeader } from "~/components/shell/app-shell";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { Switch } from "~/components/ui/choice";
import { ConfirmDialog } from "~/components/ui/confirm-dialog";
import { SimpleDialog } from "~/components/ui/dialog";
import { EmptyState, Skeleton } from "~/components/ui/feedback";
import { Field } from "~/components/ui/field";
import { Input } from "~/components/ui/input";
import { toast } from "~/components/ui/toaster";
import { api, unwrap, type SuccessBody } from "~/lib/api";
import { errorMessage, fieldErrorsOf } from "~/lib/auth";
import { cn } from "~/lib/utils";

export const Route = createFileRoute("/_app/settings/services")({
  component: ServicesPage,
});

type ServiceRow = SuccessBody<
  Awaited<ReturnType<typeof api.api.services.$get>>
>["services"][number];

const servicesQuery = queryOptions({
  queryKey: ["services"] as const,
  queryFn: () => unwrap(api.api.services.$get()),
});

function ServicesPage() {
  const queryClient = useQueryClient();
  const services = useQuery(servicesQuery);
  const [newName, setNewName] = useState("");
  const [addError, setAddError] = useState<string>();
  const [editing, setEditing] = useState<ServiceRow | null>(null);
  const [deleting, setDeleting] = useState<ServiceRow | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: servicesQuery.queryKey }),
      queryClient.invalidateQueries({ queryKey: ["public", "form-config"] }),
    ]);

  const add = useMutation({
    mutationFn: (name: string) => unwrap(api.api.services.$post({ json: { name } })),
    onSuccess: async () => {
      await refresh();
      setNewName("");
      toast.success("Service added.");
    },
    onError: (error) => setAddError(fieldErrorsOf(error).name ?? errorMessage(error)),
  });

  const toggle = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      unwrap(api.api.services[":id"].$patch({ param: { id }, json: { isActive } })),
    onSuccess: async (_data, { isActive }) => {
      await refresh();
      toast.success(isActive ? "Service shown on the form." : "Service hidden from the form.");
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const reorder = useMutation({
    mutationFn: (ids: string[]) => unwrap(api.api.services.order.$put({ json: { ids } })),
    onMutate: async (ids) => {
      await queryClient.cancelQueries({ queryKey: servicesQuery.queryKey });
      const previous = queryClient.getQueryData(servicesQuery.queryKey);
      queryClient.setQueryData(servicesQuery.queryKey, (old) =>
        old
          ? { services: ids.map((id) => old.services.find((service) => service.id === id)!) }
          : old,
      );
      return { previous };
    },
    onError: (error, _ids, context) => {
      queryClient.setQueryData(servicesQuery.queryKey, context?.previous);
      toast.error(errorMessage(error));
    },
    onSettled: refresh,
  });

  const remove = useMutation({
    mutationFn: (service: ServiceRow) =>
      unwrap(api.api.services[":id"].$delete({ param: { id: service.id } })),
    onSuccess: async (_data, service) => {
      await refresh();
      setDeleting(null);
      toast.success(`“${service.name}” was deleted.`);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    const list = services.data?.services ?? [];
    if (!over || active.id === over.id) return;
    const from = list.findIndex((service) => service.id === active.id);
    const to = list.findIndex((service) => service.id === over.id);
    if (from === -1 || to === -1) return;
    reorder.mutate(arrayMove(list, from, to).map((service) => service.id));
  };

  const nameOf = (id: string | number) =>
    services.data?.services.find((service) => service.id === id)?.name ?? "the service";
  const positionOf = (id: string | number) =>
    (services.data?.services.findIndex((service) => service.id === id) ?? -1) + 1;

  const submitNew = (event: FormEvent) => {
    event.preventDefault();
    setAddError(undefined);
    const name = newName.trim();
    if (!name) setAddError("Enter a name.");
    else add.mutate(name);
  };

  return (
    <>
      <PageHeader
        title="Services"
        description="The options people choose from on the enquiry form. Drag to reorder. Hidden services stay on existing leads."
      />
      <div className="flex max-w-3xl flex-col gap-4">
        <Card padding="sm">
          <form
            onSubmit={submitNew}
            noValidate
            className="flex flex-col gap-3 sm:flex-row sm:items-end"
          >
            <Field label="New service" error={addError} className="flex-1">
              <Input
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                maxLength={60}
              />
            </Field>
            <Button type="submit" size="sm" loading={add.isPending} className="sm:mb-1">
              <Plus /> Add service
            </Button>
          </form>
        </Card>

        <Card padding="none" className="overflow-hidden">
          {services.isPending ? (
            <div className="flex flex-col gap-3 p-5">
              {Array.from({ length: 5 }, (_, i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : services.isError ? (
            <EmptyState icon={<ListChecks />} title="Couldn't load services" />
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis, restrictToParentElement]}
              onDragEnd={onDragEnd}
              accessibility={{
                screenReaderInstructions: {
                  draggable:
                    "To reorder, press space or enter, use the up and down arrows, then press space or enter again. Escape cancels.",
                },
                announcements: {
                  onDragStart: ({ active }) => `Picked up ${nameOf(active.id)}.`,
                  onDragOver: ({ active, over }) =>
                    over ? `${nameOf(active.id)} is now at position ${positionOf(over.id)}.` : "",
                  onDragEnd: ({ active, over }) =>
                    over
                      ? `${nameOf(active.id)} moved to position ${positionOf(over.id)}.`
                      : `${nameOf(active.id)} didn't move.`,
                  onDragCancel: ({ active }) => `Cancelled. ${nameOf(active.id)} didn't move.`,
                },
              }}
            >
              <SortableContext
                items={services.data.services.map((service) => service.id)}
                strategy={verticalListSortingStrategy}
              >
                <ul aria-label="Services" className="flex flex-col divide-y divide-border">
                  {services.data.services.map((service) => (
                    <SortableServiceRow
                      key={service.id}
                      service={service}
                      onRename={() => setEditing(service)}
                      onDelete={() => setDeleting(service)}
                      onToggle={(isActive) => toggle.mutate({ id: service.id, isActive })}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          )}
        </Card>
      </div>
      <RenameDialog service={editing} onClose={() => setEditing(null)} onSaved={refresh} />
      {deleting && (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setDeleting(null)}
          title={`Delete “${deleting.name}”?`}
          description={
            deleting.leadCount
              ? `This can't be undone. ${deleting.leadCount} lead${deleting.leadCount === 1 ? "" : "s"} chose it and will be left without a service. To keep it on their records, hide it from the form instead.`
              : "This can't be undone. It won't be offered on the enquiry form any more."
          }
          confirmLabel="Delete service"
          destructive
          loading={remove.isPending}
          onConfirm={() => remove.mutate(deleting)}
        />
      )}
    </>
  );
}

function SortableServiceRow({
  service,
  onRename,
  onDelete,
  onToggle,
}: {
  service: ServiceRow;
  onRename: () => void;
  onDelete: () => void;
  onToggle: (isActive: boolean) => void;
}) {
  const {
    setNodeRef,
    setActivatorNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: service.id });
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(
        "relative flex items-center gap-3 bg-surface-solid px-3 py-3 sm:px-4",
        isDragging && "z-10 rounded-md shadow-lift ring-1 ring-primary/30",
      )}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        aria-label={`Reorder ${service.name}`}
        className="grid size-8 shrink-0 cursor-grab touch-none place-items-center rounded-sm text-fg-muted transition-colors hover:bg-surface-hover hover:text-fg focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none active:cursor-grabbing"
      >
        <GripVertical className="size-4" aria-hidden />
      </button>
      <div className="min-w-0 flex-1">
        <p
          className={service.isActive ? "text-body font-medium text-fg" : "text-body text-fg-muted"}
        >
          {service.name}
        </p>
        <p className="text-small text-fg-muted">
          {service.leadCount} lead{service.leadCount === 1 ? "" : "s"}
          {!service.isActive && (
            <Badge className="ml-2" tone="neutral">
              Hidden
            </Badge>
          )}
        </p>
      </div>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={`Rename ${service.name}`}
        onClick={onRename}
      >
        <Pencil />
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={`Delete ${service.name}`}
        onClick={onDelete}
      >
        <Trash2 />
      </Button>
      <Switch
        checked={service.isActive}
        aria-label={`Show ${service.name} on the form`}
        onCheckedChange={onToggle}
      />
    </li>
  );
}

function RenameDialog({
  service,
  onClose,
  onSaved,
}: {
  service: ServiceRow | null;
  onClose: () => void;
  onSaved: () => Promise<unknown>;
}) {
  return (
    <SimpleDialog
      open={Boolean(service)}
      onOpenChange={(open) => !open && onClose()}
      title="Rename service"
      description="Leads keep their service; they'll show the new name."
    >
      {service && (
        <RenameForm key={service.id} service={service} onClose={onClose} onSaved={onSaved} />
      )}
    </SimpleDialog>
  );
}

function RenameForm({
  service,
  onClose,
  onSaved,
}: {
  service: ServiceRow;
  onClose: () => void;
  onSaved: () => Promise<unknown>;
}) {
  const [name, setName] = useState(service.name);
  const [error, setError] = useState<string>();
  const rename = useMutation({
    mutationFn: () =>
      unwrap(
        api.api.services[":id"].$patch({ param: { id: service.id }, json: { name: name.trim() } }),
      ),
    onSuccess: async () => {
      await onSaved();
      toast.success("Service renamed.");
      onClose();
    },
    onError: (err) => setError(fieldErrorsOf(err).name ?? errorMessage(err)),
  });

  return (
    <form
      noValidate
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        setError(undefined);
        if (!name.trim()) setError("Enter a name.");
        else rename.mutate();
      }}
    >
      <Field label="Name" error={error}>
        <Input value={name} onChange={(event) => setName(event.target.value)} maxLength={60} />
      </Field>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" loading={rename.isPending}>
          Save
        </Button>
      </div>
    </form>
  );
}
